import { cloneDeep, countBy } from 'lodash';
import XlsxPopulate from 'xlsx-populate';
import { Message } from '@meitu/saas-ui';
import { getFramework, getTrackFunc } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { Layer } from '@/layers/layer';
import {
    LayerType,
    MIN_FONT_SIZE,
    PluginType,
    type Border,
    type Cell,
    type CellStyle,
    type IExportPlugin,
    type ITableLayer,
    type ITableLayerProps,
    type RowOrColSelectedRectInfo,
} from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import { getActiveTableLayerActions } from './actions';
import tableLayerBehavior from './behavior';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    MAX_COL_COUNT,
    MAX_ROW_COUNT,
    MIN_BASE_CELL_WIDTH,
    MIN_BORDER_WIDTH,
} from './default-configs';
import { TABLE_CONTROLLER_EVENTS } from './plugins/edit/events';
import { getTableEditState, type CellsSelectedInfo } from './plugins/edit/state';
import tablePresets from './presets';
import { resetTableLayerMaterialIds } from './utils/add-or-update-layer';
import { addTableLayerByClipboard as addTableLayerByClipboardTask } from './utils/clipboard';
import fixTableLayout from './utils/fix-table-layout';
import getTableLayerByXLSX from './utils/get-table-layer-by-xlsx';
import { getTableLayerScreenshotUrl } from './utils/screenshot';
import { getCellStyle } from './utils/table';

type ValueInfo<V> = {
    /** 是否存在多个不同的值 */
    isMulti: boolean;
    /** 第一个值 */
    firstValue: V;
};

/** 表格面板展示多个单元格时使用的聚合样式信息。 */
export type CellsDisplayInfo = Required<{
    [K in keyof Omit<CellStyle, 'border' | 'color' | 'backgroundColor'>]: NonNullable<CellStyle[K]>;
}> & {
    textList: string[];
    color: ValueInfo<`#${string}`>;
    backgroundColor: ValueInfo<`#${string}`>;
    layout: {
        /** 网格的宽度 */
        hSize: ValueInfo<number>;
        /** 网格的高度 */
        vSize: ValueInfo<number>;
    };
};

/** 表格面板消费的当前激活表格状态快照。 */
export interface ActiveTableInfo {
    hasSelectedInfo: boolean;
    rowCount: number;
    colCount: number;
    opacity: number;
    allCellsStyleInfo: CellsDisplayInfo | null;
    mergeInfo: {
        selectedCellsIsCanMerged: boolean;
        tableIsCanMerged: boolean;
        selectedCellsIsCanCancelMerged: boolean;
        tableIsCanCancelMerged: boolean;
    } | null;
    presetId?: string;
    selectedCellStyleInfo: CellsDisplayInfo | null;
    debugInfo: {
        cellsSelectedInfo: CellsSelectedInfo | null;
        rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
    };
}

const getCellsStyleInfo = (
    tableLayerData: ITableLayerProps,
    /** 没传cellIds时, 则代表针对整个table的所有cell */
    cellIds?: string[],
): CellsDisplayInfo | null => {
    const {
        cells: cellsProp,
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
    } = tableLayerData;
    const allCells = cellsProp.flat().filter(Boolean) as Cell[];
    const targetCells = cellIds?.length ? allCells.filter((c) => cellIds.includes(c.__renderId)) : allCells;
    const firstCell = targetCells[0];
    const firstCellStyle = getCellStyle(firstCell, tableLayerData);

    const hSizesCountMap = countBy(horizontalSizes);
    const vSizesCountMap = countBy(verticalSizes);
    const result: CellsDisplayInfo = {
        textList: targetCells.map((c) => c.content),
        fontSize: firstCellStyle.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
        textAlign: firstCellStyle.textAlign ?? DEFAULT_TABLE_CELL__TEXT_ALIGN,
        verticalAlign: firstCellStyle.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
        fontFamily: firstCellStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name,
        bold: firstCellStyle.bold ?? false,
        italic: firstCellStyle.italic ?? false,
        lineThrough: firstCellStyle.lineThrough ?? false,
        underline: firstCellStyle.underline ?? false,
        disableAutoWrap: firstCellStyle.disableAutoWrap ?? false,
        color: {
            isMulti: false,
            firstValue: (firstCellStyle.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR) as `#${string}`,
        },
        backgroundColor: {
            isMulti: false,
            firstValue: (firstCellStyle.backgroundColor ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR) as `#${string}`,
        },
        layout: {
            hSize: {
                isMulti: Object.keys(hSizesCountMap).length > 1,
                firstValue: horizontalSizes[0],
            },
            vSize: {
                isMulti: Object.keys(vSizesCountMap).length > 1,
                firstValue: verticalSizes[0],
            },
        },
    };

    for (const cell of targetCells) {
        if (result.color.isMulti && result.backgroundColor.isMulti) break;

        const style = getCellStyle(cell, tableLayerData);
        const thisColor = style.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR;
        const thisBackgroundColor = style.backgroundColor ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR;
        if (!result.color.isMulti && result.color.firstValue !== thisColor) {
            result.color.isMulti = true;
        }
        if (!result.backgroundColor.isMulti && result.backgroundColor.firstValue !== thisBackgroundColor) {
            result.backgroundColor.isMulti = true;
        }
        if (result.color.isMulti && result.backgroundColor.isMulti) break;
    }

    return result;
};

/** 表格图层对业务侧暴露的创建、导入、状态查询和事件订阅能力。 */
export class TableLayer extends Layer {
    constructor(frameworkIdentity: string) {
        super(frameworkIdentity);
        this.framework = getFramework(frameworkIdentity);
        this.fixTableLayout = this.fixTableLayout.bind(this);
        this.addTableLayerByPreset = this.addTableLayerByPreset.bind(this);
        this.addTableLayerByExcel = this.addTableLayerByExcel.bind(this);
        this.addTableLayerByClipboard = this.addTableLayerByClipboard.bind(this);
        this.getSelectedCellStyleInfo = this.getSelectedCellStyleInfo.bind(this);
        this.getTableInfo = this.getTableInfo.bind(this);
        this.getTableInfoOnUpdate = this.getTableInfoOnUpdate.bind(this);
        this.onSelectedInfoUpdate = this.onSelectedInfoUpdate.bind(this);
        this.onIsLineDraggingStateUpdate = this.onIsLineDraggingStateUpdate.bind(this);
        this.onIsTextEditingStateUpdate = this.onIsTextEditingStateUpdate.bind(this);
    }

    fixTableLayout = async <T extends ITableLayer | ITableLayer<true>>(layer: T): Promise<T> => {
        return fixTableLayout({
            originalLayer: layer,
        });
    };

    async addTableLayerByPreset(presetId: string) {
        const stageSize = this.framework.service.frame.getSizeByIndex(this.framework.service.frame.getActiveIndex());

        // 根据画布做缩放处理
        const getScaledTableLayerByStageSize = (
            tableLayer: Omit<ITableLayer<true>, 'top' | 'left'>,
        ): Omit<ITableLayer<true>, 'top' | 'left'> => {
            const scale = (() => {
                const tableMinWidth = MIN_BASE_CELL_WIDTH * tableLayer.data.baseCells.horizontal.length;
                const tableMinHeight = (tableMinWidth / tableLayer.height) * tableLayer.width;
                const tableMaxWidth = Math.max(stageSize.width * 0.6, tableMinWidth);
                const tableMaxHeight = Math.max(stageSize.height * 0.6, tableMinHeight);
                const scale =
                    tableMaxWidth / tableMaxHeight < tableLayer.width / tableLayer.height
                        ? tableMaxWidth / tableLayer.width
                        : tableMaxHeight / tableLayer.height;
                return scale;
            })();

            const newTableLayer = cloneDeep(tableLayer);
            const {
                baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
                styles,
            } = newTableLayer.data;
            Object.values(styles).forEach((style) => {
                if (style.fontSize) {
                    style.fontSize = Math.max(MIN_FONT_SIZE, Math.round(style.fontSize * scale));
                }
                if (style.border) {
                    const cellTotalBorders = [
                        style.border.top,
                        style.border.left,
                        style.border.right,
                        style.border.bottom,
                    ].filter(Boolean) as Border[];
                    cellTotalBorders.forEach(
                        (b) => (b.width = Math.max(MIN_BORDER_WIDTH, Math.round(b.width * scale))),
                    );
                }
            });
            newTableLayer.width *= scale;
            newTableLayer.height *= scale;
            horizontalSizes.forEach((_, index) => {
                horizontalSizes[index] *= scale;
            });
            verticalSizes.forEach((_, index) => {
                verticalSizes[index] *= scale;
            });
            return newTableLayer;
        };

        const targetPreset = tablePresets.find((preset) => preset.id === presetId);
        if (!targetPreset) return;

        const presetTableLayer = targetPreset.create();
        let tableLayerWithoutPosition: Omit<ITableLayer<true>, 'top' | 'left'> = {
            ...presetTableLayer,
        };
        tableLayerWithoutPosition = getScaledTableLayerByStageSize(tableLayerWithoutPosition);
        const tableLayer: ITableLayer<true> = {
            ...tableLayerWithoutPosition,
            top: 0,
            left: 0,
        };
        const newLayer = await this.fixTableLayout(cloneDeep(tableLayer as any as ITableLayer));
        resetTableLayerMaterialIds(this.framework.identity, newLayer);
        this.addByDiagonal(newLayer);
    }

    async addTableLayerByExcel(file: File) {
        const getData = (data: File) =>
            new Promise<ArrayBuffer>((resolve, reject) => {
                const fileReader = new FileReader();
                fileReader.readAsArrayBuffer(data);
                fileReader.onloadend = () => {
                    const binaryData = fileReader.result;
                    if (binaryData) {
                        resolve(binaryData as ArrayBuffer);
                    } else {
                        reject(new Error('fileReader.result is null'));
                    }
                };
                fileReader.onerror = (error) => {
                    reject(error);
                };
            });

        const data = await getData(file);

        const { $t } = getLocaleStore();
        const frameworkIdentity = this.framework.identity;
        const stageSize = this.framework.service.frame.getSizeByIndex(this.framework.service.frame.getActiveIndex());
        const stageScale = this.framework.service.edgelessCanvas.zoom;
        try {
            const sheetOfXlsxPopulate = await XlsxPopulate.fromDataAsync(data)
                .then((workbook) => workbook.sheet(0)!)
                .catch((error) => {
                    console.log('===获取xlsx样式失败', error, '\n降级处理, 直接不还原xlsx样式');
                    return null;
                });
            const xlsx = await import('xlsx');
            const { Sheets, SheetNames } = xlsx.read(data, {
                // 保留日期对象
                cellDates: true,
                type: 'buffer',
            });
            const worksheet = Sheets[SheetNames[0]];
            const { tableLayer, isExceed } = await getTableLayerByXLSX(
                frameworkIdentity,
                {
                    worksheet,
                    sheetOfXlsxPopulate,
                },
                {
                    width: stageSize.width,
                    height: stageSize.height,
                    scale: stageScale,
                },
            );
            if (isExceed) {
                Message.info($t((v, { r }) => r(v.tableLayer.hooks.supportText, [MAX_ROW_COUNT, MAX_COL_COUNT])));
            }
            const newLayer = cloneDeep(tableLayer as any as ITableLayer);
            newLayer.data.screenshotUrl = await getTableLayerScreenshotUrl(
                tableLayer as any as ITableLayer,
                frameworkIdentity,
            );
            resetTableLayerMaterialIds(this.framework.identity, newLayer);
            this.add({
                layer: newLayer,
                trackFunc: getTrackFunc(this.framework.identity, newLayer, 'addByImport'),
            });
        } catch (error) {
            console.log('===表格解析失败', error);
            Message.error($t((v) => v.tableLayer.hooks.parseFailed));
            throw error;
        }
    }

    async addTableLayerByClipboard({
        clipboardText,
        clipboardHTML,
    }: {
        clipboardText: string;
        clipboardHTML?: string;
    }): Promise<void> {
        await addTableLayerByClipboardTask({
            frameworkIdentity: this.framework.identity,
            clipboardText,
            clipboardHTML,
            addLayer: this.addByDiagonal.bind(this),
        });
    }

    getSelectedCellStyleInfo = (layerData: ITableLayerProps): CellsDisplayInfo | null => {
        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(this.framework.identity);
        const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
        if (!hasSelectedInfo) return null;
        let selectedCellIds: string[];
        if (cellsSelectedInfo) {
            selectedCellIds = cellsSelectedInfo?.cellInfoList.map((c) => c.id) ?? [];
            const cellsSelectedInfoIsSure = layerData.cells
                .flat()
                .some((c) => c && selectedCellIds.includes(c.__renderId));
            // 切换表格时, cellsSelectedInfo可能会优先于layerData更新, 会导致cellsSelectedInfo值不对, 实际上应该会null, 这里做一层校验
            if (!cellsSelectedInfoIsSure) return null;
        } else {
            const startHIndex =
                rowOrColSelectedRectInfo!.direction === 'vertical' ? rowOrColSelectedRectInfo!.index : 0;
            const startVIndex =
                rowOrColSelectedRectInfo!.direction === 'horizontal' ? rowOrColSelectedRectInfo!.index : 0;
            const endHIndex =
                rowOrColSelectedRectInfo!.direction === 'vertical'
                    ? rowOrColSelectedRectInfo!.index + 1
                    : layerData.baseCells.horizontal.length;
            const endVIndex =
                rowOrColSelectedRectInfo!.direction === 'horizontal'
                    ? rowOrColSelectedRectInfo!.index + 1
                    : layerData.baseCells.vertical.length;
            const targetCells = layerData.cells
                .slice(startVIndex, endVIndex)
                .map((row) => row.slice(startHIndex, endHIndex))
                .flat()
                .filter(Boolean) as Cell[];
            selectedCellIds = targetCells.map((c) => c.__renderId);
        }
        return getCellsStyleInfo(layerData, selectedCellIds);
    };

    /**
     * 表格操作面板需要展示的相关信息
     * 防卡死的优化:
     * 1. 移动时不需要更新ActiveTableInfo
     * 2. 行列拖拽时不需要更新ActiveTableInfo
     * 3. 文字编辑时不需要更新ActiveTableInfo
     */
    getTableInfo(layer: ITableLayer): ActiveTableInfo {
        const tableLayerData = layer.data;
        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(this.framework.identity);

        const checkCellIsMerged = (cell: Record<'colSpan' | 'rowSpan', number>): boolean =>
            cell.colSpan > 1 || cell.rowSpan > 1;

        const getMergeInfo = (): ActiveTableInfo['mergeInfo'] => {
            const {
                cells: cellsProp,
                baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
            } = tableLayerData;

            const realCellsLength = cellsProp.flat().filter(Boolean).length;

            const mergeInfo: ActiveTableInfo['mergeInfo'] = {
                selectedCellsIsCanMerged: true,
                tableIsCanMerged: realCellsLength > 1,
                selectedCellsIsCanCancelMerged: true,
                tableIsCanCancelMerged: realCellsLength === 1 && checkCellIsMerged(tableLayerData.cells[0][0]!),
            };

            const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
            if (hasSelectedInfo) {
                let cells: Cell[];
                if (cellsSelectedInfo) {
                    cells = cellsSelectedInfo.cellInfoList.map((c) => cellsProp[c.vIndex][c.hIndex]!);
                } else {
                    const startHIndex =
                        rowOrColSelectedRectInfo!.direction === 'vertical' ? rowOrColSelectedRectInfo!.index : 0;
                    const startVIndex =
                        rowOrColSelectedRectInfo!.direction === 'horizontal' ? rowOrColSelectedRectInfo!.index : 0;
                    const endHIndex =
                        rowOrColSelectedRectInfo!.direction === 'vertical'
                            ? rowOrColSelectedRectInfo!.index + 1
                            : horizontalSizes.length;
                    const endVIndex =
                        rowOrColSelectedRectInfo!.direction === 'horizontal'
                            ? rowOrColSelectedRectInfo!.index + 1
                            : verticalSizes.length;
                    cells = cellsProp
                        .slice(startVIndex, endVIndex)
                        .map((row) => row.slice(startHIndex, endHIndex))
                        .filter(Boolean)
                        .flat() as Cell[];
                }
                if (!(cells.length === 1 && cells[0] && checkCellIsMerged(cells[0]))) {
                    mergeInfo.selectedCellsIsCanCancelMerged = false;
                }
                if (cells.length <= 1) {
                    mergeInfo.selectedCellsIsCanMerged = false;
                }
                if (rowOrColSelectedRectInfo) {
                    /** 判断是不是都在选中的行/列中, 并且填满了, 不是的话就不能合并/取消合并 */
                    const checkIsFullRect = (): boolean => {
                        let isFullRect = true;
                        if (rowOrColSelectedRectInfo.direction === 'horizontal') {
                            const rowCells = cellsProp[rowOrColSelectedRectInfo.index].filter(Boolean) as Cell[];
                            for (let hIndex = 0; hIndex < horizontalSizes.length; hIndex++) {
                                const isContain = rowCells.some(
                                    (c) => c.hIndex <= hIndex && hIndex + 1 <= c.hIndex + c.colSpan && c.rowSpan === 1,
                                );
                                if (!isContain) {
                                    isFullRect = false;
                                    break;
                                }
                            }
                        } else {
                            const colCells = cellsProp
                                .map((rowCells) => rowCells[rowOrColSelectedRectInfo.index])
                                .filter(Boolean) as Cell[];
                            for (let vIndex = 0; vIndex < verticalSizes.length; vIndex++) {
                                const isContain = colCells.some(
                                    (c) => c?.vIndex <= vIndex && vIndex < c?.vIndex + c.rowSpan && c.colSpan === 1,
                                );
                                if (!isContain) {
                                    isFullRect = false;
                                    break;
                                }
                            }
                        }
                        return isFullRect;
                    };
                    if (!checkIsFullRect()) {
                        mergeInfo.selectedCellsIsCanMerged = false;
                        mergeInfo.selectedCellsIsCanCancelMerged = false;
                    }
                }
            } else {
                mergeInfo.selectedCellsIsCanMerged = false;
                mergeInfo.selectedCellsIsCanCancelMerged = false;
            }
            return mergeInfo;
        };

        const activeTableInfo: ActiveTableInfo = {
            hasSelectedInfo: !!cellsSelectedInfo || !!rowOrColSelectedRectInfo,
            mergeInfo: getMergeInfo(),
            rowCount: tableLayerData.baseCells.vertical.length,
            colCount: tableLayerData.baseCells.horizontal.length,
            opacity: layer.opacity ?? 1,
            allCellsStyleInfo: getCellsStyleInfo(tableLayerData),
            presetId: tableLayerData.presetId,
            selectedCellStyleInfo: this.getSelectedCellStyleInfo(tableLayerData),
            debugInfo: {
                cellsSelectedInfo,
                rowOrColSelectedRectInfo,
            },
        };
        return activeTableInfo;
    }

    /** react数据更新时, 可能会导致临时数据对不上, 需要用这个函数兜底 */
    getTableInfoOnUpdate = (layer: ITableLayer): ActiveTableInfo | null => {
        try {
            return this.getTableInfo(layer);
        } catch {
            return null;
        }
    };

    activeTableLayerActions = getActiveTableLayerActions(this.framework.identity);

    onSelectedInfoUpdate = (
        fn: (data: {
            cellsSelectedInfo: CellsSelectedInfo | null;
            rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
        }) => void,
    ) => {
        const listenerId = this.framework.service.events.on(TABLE_CONTROLLER_EVENTS.SELECTED_INFO_UPDATE, fn);
        return () => this.framework.service.events.off(listenerId);
    };

    onIsLineDraggingStateUpdate = (fn: (isDragging: boolean) => void) => {
        const listenerId = this.framework.service.events.on(TABLE_CONTROLLER_EVENTS.LINE_IS_DRAGGING_STATE_UPDATE, fn);
        return () => this.framework.service.events.off(listenerId);
    };

    onIsTextEditingStateUpdate = (fn: (isEditing: boolean) => void) => {
        const listenerId = this.framework.service.events.on(TABLE_CONTROLLER_EVENTS.TABLE_EDIT_STATE_CHANGE, fn);
        return () => this.framework.service.events.off(listenerId);
    };
}

/**
 * 表格图层插件
 */
export const tableLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.TABLE,
    createInstance({ identity }) {
        return [{ key: 'TableLayer', value: new TableLayer(identity) }];
    },
    createBehavior() {
        return tableLayerBehavior;
    },
};

export { tableLayerEditPlugin, type TableAction } from './plugins/edit';
